import { useState, useEffect } from "react";
import type { MomentCoupon, MomentMessageType } from "../../data/momentOptions";
import { SectionBox, SelectBox, inputCls, inputSmCls } from "../shared";

type Props = {
  messageType: MomentMessageType;
  couponEnabled: boolean;
  coupon: MomentCoupon;
  onCouponEnabledChange: (val: boolean) => void;
  onCouponChange: (key: keyof MomentCoupon, value: string) => void;
};

function getCouponTitleLimit(messageType: MomentMessageType) {
  return messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE" ? 12 : 7;
}

function getCouponDescriptionLimit(messageType: MomentMessageType) {
  return messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE" ? 18 : 12;
}

export default function CouponSection({
  messageType, couponEnabled, coupon, onCouponEnabledChange, onCouponChange,
}: Props) {
  const [couponPcEnabled, setCouponPcEnabled] = useState(coupon.pcLandingUrl.trim().length > 0);
  const couponTitleLimit = getCouponTitleLimit(messageType);
  const couponDescriptionLimit = getCouponDescriptionLimit(messageType);
  const isShippingCoupon = coupon.couponBookTitleType === "FREE_SHIPPING";
  const isPriceCoupon = coupon.couponBookTitleType === "DISCOUNT_PRICE";
  const isRateCoupon = coupon.couponBookTitleType === "DISCOUNT_RATE";

  const couponTitlePlaceholder =
    isPriceCoupon ? "할인금액 숫자 입력"
    : isRateCoupon ? "할인율 숫자 입력"
    : coupon.couponBookTitleType === "FREE_GIFT" ? `쿠폰 타이틀 (최대 ${couponTitleLimit}자)`
    : coupon.couponBookTitleType === "UPGRADE" ? `쿠폰 타이틀 (최대 ${couponTitleLimit}자)`
    : "쿠폰 타이틀";

  useEffect(() => {
    setCouponPcEnabled(coupon.pcLandingUrl.trim().length > 0);
  }, [coupon.pcLandingUrl]);

  const handleCouponPcToggle = (checked: boolean) => {
    setCouponPcEnabled(checked);
    if (!checked) onCouponChange("pcLandingUrl", "");
  };

  return (
    <SectionBox label="쿠폰" enabled={couponEnabled} onToggle={onCouponEnabledChange}>
      <div className="tw-space-y-2">
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <div className="tw-space-y-1.5">
            <input
              value={coupon.couponBookTitle}
              onChange={(e) => {
                const rawValue = e.target.value;
                let nextValue = rawValue;
                if (isPriceCoupon) nextValue = rawValue.replace(/\D/g, "").slice(0, 8);
                else if (isRateCoupon) nextValue = rawValue.replace(/\D/g, "").slice(0, 2);
                else nextValue = rawValue.slice(0, couponTitleLimit);
                onCouponChange("couponBookTitle", nextValue);
              }}
              placeholder={couponTitlePlaceholder}
              disabled={isShippingCoupon}
              className={inputSmCls}
            />
            {!isShippingCoupon && (
              <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                <span>
                  {coupon.couponBookTitle.length} / {isPriceCoupon ? 8 : isRateCoupon ? 2 : couponTitleLimit}
                </span>
              </div>
            )}
          </div>
          <SelectBox
            value={coupon.couponBookTitleType}
            onChange={(val) => {
              onCouponChange("couponBookTitleType", val);
              onCouponChange("couponBookTitle", "");
            }}
            options={[
              { value: "DISCOUNT_PRICE", label: "할인금액" },
              { value: "DISCOUNT_RATE", label: "할인율" },
              { value: "FREE_SHIPPING", label: "배송비 할인" },
              { value: "FREE_GIFT", label: "무료 증정" },
              { value: "UPGRADE", label: "업그레이드" },
            ]}
            className="tw-w-full"
          />
        </div>
        {isShippingCoupon && (
          <p className="tw-text-xs tw-text-gray-400">배송비 할인 쿠폰은 쿠폰 타이틀을 입력하지 않습니다.</p>
        )}
        <input
          value={coupon.title}
          onChange={(e) => onCouponChange("title", e.target.value.slice(0, couponDescriptionLimit))}
          placeholder={`쿠폰 상세 설명 (최대 ${couponDescriptionLimit}자)`}
          className={inputCls}
        />
        <p className="tw-text-xs tw-text-gray-400">{coupon.title.length} / {couponDescriptionLimit}</p>
        <div>
          <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
            <span className="tw-text-red-500 tw-mr-1">*</span>
            모바일 랜딩 URL
          </label>
          <input value={coupon.mobileLandingUrl} onChange={(e) => onCouponChange("mobileLandingUrl", e.target.value)} placeholder="https://" className={inputCls} />
        </div>
        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
          <input type="checkbox" checked={couponPcEnabled} onChange={(e) => handleCouponPcToggle(e.target.checked)} className="tw-w-3.5 tw-h-3.5" />
          PC 링크
        </label>
        {couponPcEnabled && (
          <div>
            <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">PC 랜딩 URL</label>
            <input value={coupon.pcLandingUrl} onChange={(e) => onCouponChange("pcLandingUrl", e.target.value)} placeholder="https://" className={inputCls} />
          </div>
        )}
      </div>
    </SectionBox>
  );
}
